import React, { useState, useEffect } from "react";
import { Plus, Trash2, ExternalLink, Edit2, Check, X, Search, Filter } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

// ─── Stable client-side UID for editable rows ──────────────────────────────
// Backfills _uid on rows missing one, so React `key` stays stable across
// edits / sort / delete (avoids stale state with array index keys).
const newUid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `r-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

function useStableUids(data, onChange) {
  useEffect(() => {
    if (Array.isArray(data) && data.some(d => d && !d._uid)) {
      onChange(data.map(d => (d && d._uid ? d : { ...d, _uid: newUid() })));
    }
  }, [data]);
}

// ─── Importance (1-5): badge + dot-scale visuale ──────────────────────────
const IMP_COLORS = {
  1: "#6B7280",  // info
  2: "#60A5FA",  // basso
  3: "#0891B2",  // medio
  4: "#EA580C",  // alto
  5: "#DC2626",  // critico
};
const IMP_LABELS = { 1: "INFO", 2: "BASSO", 3: "MEDIO", 4: "ALTO", 5: "CRITICO" };
const IMP_LABEL_KEYS = { 1: "importance.1", 2: "importance.2", 3: "importance.3", 4: "importance.4", 5: "importance.5" };

export function ImportanceBadge({ value }) {
  const { t } = useLanguage();
  const v = Number(value) || 3;
  const color = IMP_COLORS[v];
  const label = t(IMP_LABEL_KEYS[v]);
  return (
    <span
      title={`Importanza: ${label}`}
      className="flex items-center gap-1.5 px-2 py-0.5 border rounded-full"
      style={{ borderColor: `${color}50`, backgroundColor: `${color}12` }}
    >
      <span className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <span key={i} className="w-1 h-1 rounded-full"
            style={{ backgroundColor: i <= v ? color : "#1F242D" }} />
        ))}
      </span>
      <span className="text-[9px] font-bold tracking-widest uppercase" style={{ color }}>
        {label}
      </span>
    </span>
  );
}

function ImportanceSelect({ value, onChange }) {
  const { t } = useLanguage();
  const v = Number(value) || 3;
  return (
    <select
      value={v}
      onChange={(e) => onChange(Number(e.target.value))}
      className="bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]"
    >
      {[1, 2, 3, 4, 5].map(i => (
        <option key={i} value={i}>{i} – {t(IMP_LABEL_KEYS[i])}</option>
      ))}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────
// SOCMINT Panel – Social Media platforms
// ─────────────────────────────────────────────────────────────
export const PLATFORMS = [
  { key: "linkedin",  label: "LinkedIn",  color: "#0A66C2", bg: "#0A66C2" },
  { key: "twitter",   label: "X / Twitter", color: "#1DA1F2", bg: "#1DA1F2" },
  { key: "instagram", label: "Instagram",  color: "#E1306C", bg: "#E1306C" },
  { key: "facebook",  label: "Facebook",   color: "#1877F2", bg: "#1877F2" },
  { key: "youtube",   label: "YouTube",    color: "#FF0000", bg: "#FF0000" },
];

const VISIBILITY_OPTS = ["Pubblico", "Parziale", "Privato", "Non presente"];

function SocmintPanel({ data = [], onChange, filter, sort }) {
  const { t } = useLanguage();
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({});
  useStableUids(data, onChange);

  const startEdit = (idx) => { setEditIdx(idx); setForm({ ...data[idx] }); };
  const cancelEdit = () => { setEditIdx(null); setForm({}); };
  const saveEdit = () => {
    const updated = data.map((d, i) => i === editIdx ? { ...form, _uid: d._uid || form._uid || newUid() } : d);
    onChange(updated);
    cancelEdit();
  };
  const addPlatform = () => {
    const used = data.map(d => d.platform);
    const next = PLATFORMS.find(p => !used.includes(p.key));
    if (!next) return;
    const newEntry = { _uid: newUid(), platform: next.key, username: "", followers: "", visibility: "Pubblico", last_activity: "", public_info: "", url: "", importance: 3 };
    onChange([...data, newEntry]);
    setEditIdx(data.length);
    setForm(newEntry);
  };
  const removeRow = (idx) => onChange(data.filter((_, i) => i !== idx));

  const visibleCount = filter ? data.filter(filter).length : data.length;
  const hiddenCount = data.length - visibleCount;
  const displayItems = data
    .map((row, idx) => ({ row, idx }))
    .filter(({ row, idx }) => !filter || filter(row) || editIdx === idx)
    .sort((a, b) => (sort ? sort(a.row, b.row) : 0));

  return (
    <div className="space-y-3">
      {hiddenCount > 0 && (
        <div className="text-[10px] text-[#A0AEC0] tracking-widest uppercase px-1">
          {visibleCount} {t("toolbar.of")} {data.length} {t("intel.entries")} — {hiddenCount} {t("toolbar.hidden_by_filter")}
        </div>
      )}
      <div className="grid grid-cols-1 gap-3">
        {displayItems.map(({ row, idx }) => {
          const plat = PLATFORMS.find(p => p.key === row.platform) || { label: row.platform, color: "#8B949E" };
          return (
            <div key={row._uid || idx} data-testid={`socmint-row-${idx}`} className="bg-[#050505] border border-white/[0.04] p-4 hover:border-white/[0.06] transition-colors">
              {editIdx === idx ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Piattaforma</label>
                      <select value={form.platform} onChange={e => setForm(p => ({...p, platform: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00FF66]">
                        {PLATFORMS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Username / Handle</label>
                      <input value={form.username} onChange={e => setForm(p => ({...p, username: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00FF66]" placeholder="@username" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Followers</label>
                      <input value={form.followers} onChange={e => setForm(p => ({...p, followers: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00FF66]" placeholder="es. 4.200" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Visibilità Profilo</label>
                      <select value={form.visibility} onChange={e => setForm(p => ({...p, visibility: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00FF66]">
                        {VISIBILITY_OPTS.map(v => <option key={v}>{v}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Ultima Attività</label>
                      <input value={form.last_activity} onChange={e => setForm(p => ({...p, last_activity: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00FF66]" placeholder="es. 2025-03-15" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">URL Profilo</label>
                      <input value={form.url} onChange={e => setForm(p => ({...p, url: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00FF66]" placeholder="https://..." />
                    </div>
                    <div>
                      <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Importanza</label>
                      <ImportanceSelect value={form.importance} onChange={(v) => setForm(p => ({...p, importance: v}))} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Informazioni Pubbliche / Note</label>
                    <textarea value={form.public_info} onChange={e => setForm(p => ({...p, public_info: e.target.value}))} rows={2} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00FF66] resize-none" placeholder="Post pubblici, bio, interessi, gruppi..." style={{fontFamily: "JetBrains Mono, monospace"}} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="flex items-center gap-1 px-3 py-1.5 bg-[#00FF66] text-black text-xs font-semibold hover:bg-[#00CC55]"><Check className="w-3 h-3" /> Salva</button>
                    <button onClick={cancelEdit} className="flex items-center gap-1 px-3 py-1.5 bg-[#1F242D] text-white text-xs border border-white/[0.06] hover:bg-[#30363D]"><X className="w-3 h-3" /> Annulla</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-sm flex-shrink-0 mt-0.5" style={{backgroundColor: `${plat.color}20`, border: `1px solid ${plat.color}40`}}>
                    <span className="text-[10px] font-bold" style={{color: plat.color}}>{plat.label.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-semibold" style={{color: plat.color}}>{plat.label}</span>
                      {row.username && <span className="text-xs text-white">{row.username}</span>}
                      {row.followers && <span className="text-[10px] px-1.5 py-0.5 bg-[#1A1D24] text-[#CBD5E1] border border-white/[0.04]">{row.followers} followers</span>}
                      <span className={`text-[10px] px-1.5 py-0.5 border ${row.visibility === "Pubblico" ? "text-[#00FF66] bg-[#00FF66]/10 border-[#00FF66]/20" : row.visibility === "Parziale" ? "text-[#FF9F0A] bg-[#FF9F0A]/10 border-[#FF9F0A]/20" : "text-[#FF3B30] bg-[#FF3B30]/10 border-[#FF3B30]/20"}`}>{row.visibility}</span>
                      {row.last_activity && <span className="text-[10px] text-[#A0AEC0]">Attivo: {row.last_activity}</span>}
                      {row.url && <a href={row.url} target="_blank" rel="noreferrer" className="text-[10px] text-[#00E5FF] hover:underline flex items-center gap-0.5"><ExternalLink className="w-2.5 h-2.5" /> Profilo</a>}
                    </div>
                    {row.public_info && <p className="text-xs text-[#CBD5E1] mt-1 leading-relaxed" style={{fontFamily: "JetBrains Mono, monospace"}}>{row.public_info}</p>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0 items-center">
                    <ImportanceBadge value={row.importance} />
                    <button onClick={() => startEdit(idx)} className="text-[#A0AEC0] hover:text-[#00FF66] transition-colors ml-2"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => removeRow(idx)} className="text-[#A0AEC0] hover:text-[#FF3B30] transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {data.length < PLATFORMS.length && (
        <button onClick={addPlatform} data-testid="add-socmint-platform" className="flex items-center gap-2 text-xs text-[#00FF66] hover:text-[#00CC55] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Aggiungi piattaforma social
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// OSINT Panel – Web sources
// ─────────────────────────────────────────────────────────────
const OSINT_CATS = ["News", "Legal", "Finanziario", "Accademico", "Blog", "Forum", "Comunicato", "Altro"];
export { OSINT_CATS };
const CAT_COLORS = { News: "#0891B2", Legal: "#DC2626", Finanziario: "#059669", Accademico: "#7C3AED", Blog: "#EA580C", Forum: "#DB2777", Comunicato: "#34D399", Altro: "#8B949E" };

function OsintPanel({ data = [], onChange, filter, sort }) {
  const { t } = useLanguage();
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newForm, setNewForm] = useState({ title: "", source: "", url: "", date: "", category: "News", excerpt: "", importance: 3 });
  useStableUids(data, onChange);

  const removeRow = (idx) => onChange(data.filter((_, i) => i !== idx));
  const startEdit = (idx) => { setEditIdx(idx); setForm({ ...data[idx] }); };
  const cancelEdit = () => { setEditIdx(null); setForm({}); };
  const saveEdit = () => { onChange(data.map((d, i) => i === editIdx ? { ...form, _uid: d._uid || form._uid || newUid() } : d)); cancelEdit(); };

  const addEntry = () => {
    if (!newForm.title && !newForm.source) return;
    onChange([...data, { ...newForm, _uid: newUid() }]);
    setNewForm({ title: "", source: "", url: "", date: "", category: "News", excerpt: "", importance: 3 });
    setShowAdd(false);
  };

  const visibleCount = filter ? data.filter(filter).length : data.length;
  const hiddenCount = data.length - visibleCount;
  const displayItems = data
    .map((row, idx) => ({ row, idx }))
    .filter(({ row, idx }) => !filter || filter(row) || editIdx === idx)
    .sort((a, b) => (sort ? sort(a.row, b.row) : 0));

  return (
    <div className="space-y-3">
      {hiddenCount > 0 && (
        <div className="text-[10px] text-[#A0AEC0] tracking-widest uppercase px-1">
          {visibleCount} {t("toolbar.of")} {data.length} {t("intel.entries")} — {hiddenCount} {t("toolbar.hidden_by_filter")}
        </div>
      )}
      {displayItems.map(({ row, idx }) => {
        const catColor = CAT_COLORS[row.category] || "#8B949E";
        return (
          <div key={row._uid || idx} data-testid={`osint-row-${idx}`} className="bg-[#050505] border border-white/[0.04] p-4 hover:border-white/[0.06] transition-colors">
            {editIdx === idx ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Titolo *</label>
                    <input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Fonte</label>
                    <input value={form.source} onChange={e => setForm(p => ({...p, source: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Categoria</label>
                    <select value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]">
                      {OSINT_CATS.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Data</label>
                    <input value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]" placeholder="YYYY-MM-DD" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">URL</label>
                    <input value={form.url} onChange={e => setForm(p => ({...p, url: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Importanza</label>
                    <ImportanceSelect value={form.importance} onChange={(v) => setForm(p => ({...p, importance: v}))} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Estratto / Note</label>
                    <textarea value={form.excerpt} onChange={e => setForm(p => ({...p, excerpt: e.target.value}))} rows={2} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF] resize-none" style={{fontFamily: "JetBrains Mono, monospace"}} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={saveEdit} className="flex items-center gap-1 px-3 py-1.5 bg-[#00E5FF] text-black text-xs font-semibold hover:bg-[#00B3CC]"><Check className="w-3 h-3" /> Salva</button>
                  <button onClick={cancelEdit} className="flex items-center gap-1 px-3 py-1.5 bg-[#1F242D] text-white text-xs border border-white/[0.06] hover:bg-[#30363D]"><X className="w-3 h-3" /> Annulla</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <span className="text-[10px] px-2 py-0.5 border flex-shrink-0 mt-0.5 tracking-wider uppercase" style={{color: catColor, borderColor: `${catColor}40`, backgroundColor: `${catColor}10`}}>{row.category}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 mb-1">
                    <p className="text-xs text-white font-medium leading-snug">{row.title}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap mt-1">
                    {row.source && <span className="text-[10px] text-[#CBD5E1] font-medium">{row.source}</span>}
                    {row.date && <span className="text-[10px] text-[#A0AEC0]">{row.date}</span>}
                    {row.url && <a href={row.url} target="_blank" rel="noreferrer" className="text-[10px] text-[#00E5FF] hover:underline flex items-center gap-0.5"><ExternalLink className="w-2.5 h-2.5" /> Fonte</a>}
                  </div>
                  {row.excerpt && <p className="text-xs text-[#CBD5E1] mt-2 leading-relaxed border-l-2 pl-2" style={{borderColor: `${catColor}40`, fontFamily: "JetBrains Mono, monospace"}}>{row.excerpt}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0 items-center">
                  <ImportanceBadge value={row.importance} />
                  <button onClick={() => startEdit(idx)} className="text-[#A0AEC0] hover:text-[#00E5FF] transition-colors ml-2"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => removeRow(idx)} className="text-[#A0AEC0] hover:text-[#FF3B30] transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {showAdd ? (
        <div className="bg-[#050505] border border-[#00E5FF]/30 p-4 space-y-3">
          <p className="text-[10px] text-[#A0AEC0] tracking-widest uppercase">Nuova fonte OSINT</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Titolo *</label>
              <input value={newForm.title} onChange={e => setNewForm(p => ({...p, title: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]" placeholder="es. Intervista su Corriere della Sera" />
            </div>
            <div>
              <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Fonte</label>
              <input value={newForm.source} onChange={e => setNewForm(p => ({...p, source: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]" placeholder="es. Corriere della Sera" />
            </div>
            <div>
              <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Categoria</label>
              <select value={newForm.category} onChange={e => setNewForm(p => ({...p, category: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]">
                {OSINT_CATS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Data</label>
              <input value={newForm.date} onChange={e => setNewForm(p => ({...p, date: e.target.value}))} type="date" className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]" />
            </div>
            <div>
              <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">URL</label>
              <input value={newForm.url} onChange={e => setNewForm(p => ({...p, url: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]" placeholder="https://..." />
            </div>
            <div>
              <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Importanza</label>
              <ImportanceSelect value={newForm.importance} onChange={(v) => setNewForm(p => ({...p, importance: v}))} />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Estratto</label>
              <textarea value={newForm.excerpt} onChange={e => setNewForm(p => ({...p, excerpt: e.target.value}))} rows={2} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF] resize-none" style={{fontFamily: "JetBrains Mono, monospace"}} />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={addEntry} className="flex items-center gap-1 px-3 py-1.5 bg-[#00E5FF] text-black text-xs font-semibold hover:bg-[#00B3CC]"><Check className="w-3 h-3" /> Aggiungi</button>
            <button onClick={() => setShowAdd(false)} className="flex items-center gap-1 px-3 py-1.5 bg-[#1F242D] text-white text-xs border border-white/[0.06] hover:bg-[#30363D]"><X className="w-3 h-3" /> Annulla</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} data-testid="add-osint-entry" className="flex items-center gap-2 text-xs text-[#00E5FF] hover:text-[#00B3CC] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Aggiungi fonte OSINT
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// HUMINT Panel – Human interactions
// ─────────────────────────────────────────────────────────────
const CHANNELS = ["Fiera", "Telefono", "Email", "Meeting", "LinkedIn", "WhatsApp", "Referral", "Altro"];
const OUTCOMES = ["Positivo", "Neutro", "Negativo", "In attesa", "Follow-up"];
const CHANNEL_ICONS = { Fiera: "T", Telefono: "P", Email: "E", Meeting: "M", LinkedIn: "L", WhatsApp: "W", Referral: "R", Altro: "A" };
const CHANNEL_COLORS = { Fiera: "#EA580C", Telefono: "#059669", Email: "#0891B2", Meeting: "#7C3AED", LinkedIn: "#0A66C2", WhatsApp: "#25D366", Referral: "#DB2777", Altro: "#8B949E" };
const OUTCOME_COLORS = { Positivo: "#059669", Neutro: "#EA580C", Negativo: "#DC2626", "In attesa": "#8B949E", "Follow-up": "#0891B2" };

// Extracted from HumintPanel: must NOT be defined during render so React can
// preserve focus/state between keystrokes on each input.
function InteractionForm({ f, setF, onSave, onCancel }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Data *</label>
          <input value={f.date} onChange={e => setF(p => ({...p, date: e.target.value}))} type="date" className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#FF9F0A]" />
        </div>
        <div>
          <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Canale</label>
          <select value={f.channel} onChange={e => setF(p => ({...p, channel: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#FF9F0A]">
            {CHANNELS.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Evento / Contesto</label>
          <input value={f.event_name} onChange={e => setF(p => ({...p, event_name: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#FF9F0A]" placeholder="es. SMAU 2024" />
        </div>
        <div>
          <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Contatto</label>
          <input value={f.contact_person} onChange={e => setF(p => ({...p, contact_person: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#FF9F0A]" placeholder="es. Luca Ferrari" />
        </div>
        <div>
          <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Esito</label>
          <select value={f.outcome} onChange={e => setF(p => ({...p, outcome: e.target.value}))} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#FF9F0A]">
            {OUTCOMES.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Importanza</label>
          <ImportanceSelect value={f.importance} onChange={(v) => setF(p => ({...p, importance: v}))} />
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">Note / Contenuto Interazione</label>
          <textarea value={f.notes} onChange={e => setF(p => ({...p, notes: e.target.value}))} rows={3} className="w-full bg-[#0D0F12] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#FF9F0A] resize-none" placeholder="Descrivi il contenuto dell'interazione..." style={{fontFamily: "JetBrains Mono, monospace"}} />
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={onSave} className="flex items-center gap-1 px-3 py-1.5 bg-[#FF9F0A] text-black text-xs font-semibold hover:bg-[#E08E09]"><Check className="w-3 h-3" /> Salva</button>
        <button onClick={onCancel} className="flex items-center gap-1 px-3 py-1.5 bg-[#1F242D] text-white text-xs border border-white/[0.06] hover:bg-[#30363D]"><X className="w-3 h-3" /> Annulla</button>
      </div>
    </div>
  );
}

function HumintPanel({ data = [], onChange, filter, sort }) {
  const { t } = useLanguage();
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newForm, setNewForm] = useState({ date: "", channel: "Fiera", event_name: "", contact_person: "", notes: "", outcome: "Neutro", importance: 3 });
  useStableUids(data, onChange);

  const removeRow = (idx) => onChange(data.filter((_, i) => i !== idx));
  const startEdit = (idx) => { setEditIdx(idx); setForm({ ...data[idx] }); };
  const cancelEdit = () => { setEditIdx(null); setForm({}); };
  const saveEdit = () => { onChange(data.map((d, i) => i === editIdx ? { ...form, _uid: d._uid || form._uid || newUid() } : d)); cancelEdit(); };

  const addEntry = () => {
    if (!newForm.date && !newForm.notes) return;
    const sorted = [...data, { ...newForm, _uid: newUid() }].sort((a, b) => b.date.localeCompare(a.date));
    onChange(sorted);
    setNewForm({ date: "", channel: "Fiera", event_name: "", contact_person: "", notes: "", outcome: "Neutro", importance: 3 });
    setShowAdd(false);
  };

  const defaultSort = (a, b) => (b.date || "").localeCompare(a.date || "");
  const sorted = [...data].sort(sort || defaultSort);
  const visibleSorted = filter ? sorted.filter(filter) : sorted;
  const hiddenCount = sorted.length - visibleSorted.length;

  return (
    <div className="space-y-3">
      {hiddenCount > 0 && (
        <div className="text-[10px] text-[#A0AEC0] tracking-widest uppercase px-1 ml-10">
          {visibleSorted.length} {t("toolbar.of")} {sorted.length} {t("intel.entries")} — {hiddenCount} {t("toolbar.hidden_by_filter")}
        </div>
      )}
      {/* Timeline */}
      <div className="relative">
        {visibleSorted.length > 0 && <div className="absolute left-4 top-0 bottom-0 w-px bg-[#1F242D]" />}
        <div className="space-y-3">
          {visibleSorted.map((row, idx) => {
            const realIdx = data.findIndex(d => d === row);
            const chColor = CHANNEL_COLORS[row.channel] || "#8B949E";
            const outColor = OUTCOME_COLORS[row.outcome] || "#8B949E";
            return (
              <div key={row._uid || idx} data-testid={`humint-row-${idx}`} className="relative pl-10">
                <div className="absolute left-2 top-3 w-5 h-5 flex items-center justify-center text-[9px] font-bold border border-white/[0.04]" style={{backgroundColor: `${chColor}20`, color: chColor, borderColor: `${chColor}40`}}>
                  {CHANNEL_ICONS[row.channel] || "A"}
                </div>
                <div className="bg-[#050505] border border-white/[0.04] p-4 hover:border-white/[0.06] transition-colors">
                  {editIdx === realIdx ? (
                    <InteractionForm f={form} setF={setForm} onSave={saveEdit} onCancel={cancelEdit} />
                  ) : (
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] px-2 py-0.5 border tracking-wider uppercase" style={{color: chColor, borderColor: `${chColor}40`, backgroundColor: `${chColor}10`}}>{row.channel}</span>
                          {row.event_name && <span className="text-xs text-white font-medium">{row.event_name}</span>}
                          {row.date && <span className="text-[10px] text-[#A0AEC0]">{row.date}</span>}
                          <span className="text-[10px] px-1.5 py-0.5 border" style={{color: outColor, borderColor: `${outColor}40`, backgroundColor: `${outColor}10`}}>{row.outcome}</span>
                          <ImportanceBadge value={row.importance} />
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => startEdit(realIdx)} className="text-[#A0AEC0] hover:text-[#FF9F0A] transition-colors"><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => removeRow(realIdx)} className="text-[#A0AEC0] hover:text-[#FF3B30] transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                      {row.contact_person && <p className="text-[10px] text-[#A0AEC0] mb-1">Contatto: <span className="text-[#CBD5E1]">{row.contact_person}</span></p>}
                      {row.notes && <p className="text-xs text-[#CBD5E1] leading-relaxed" style={{fontFamily: "JetBrains Mono, monospace"}}>{row.notes}</p>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showAdd ? (
        <div className="bg-[#050505] border border-[#FF9F0A]/30 p-4 ml-10">
          <p className="text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-3">Nuova interazione HUMINT</p>
          <InteractionForm f={newForm} setF={setNewForm} onSave={addEntry} onCancel={() => setShowAdd(false)} />
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} data-testid="add-humint-entry" className="flex items-center gap-2 text-xs text-[#FF9F0A] hover:text-[#E08E09] transition-colors ml-10">
          <Plus className="w-3.5 h-3.5" /> Aggiungi interazione
        </button>
      )}
    </div>
  );
}

export { SocmintPanel, OsintPanel, HumintPanel };

// ─────────────────────────────────────────────────────────────
// IntelligenceToolbar – filtering UI
// ─────────────────────────────────────────────────────────────
const HUMINT_CHANNELS_ALL = ["Fiera", "Telefono", "Email", "Meeting", "LinkedIn", "WhatsApp", "Referral", "Altro"];

export function IntelligenceToolbar({ filters, setFilters, counts }) {
  const { t } = useLanguage();
  const reset = () => setFilters({ text: "", minImportance: 0, platform: "all", category: "all", channel: "all", sort: "default" });
  const active =
    (filters.text || "").trim().length > 0 ||
    filters.minImportance > 0 ||
    filters.platform !== "all" ||
    filters.category !== "all" ||
    filters.channel !== "all" ||
    (filters.sort && filters.sort !== "default");

  return (
    <div
      data-testid="intelligence-toolbar"
      className="bg-[#0D0F12] border border-white/[0.04] p-3 flex flex-wrap items-end gap-3"
    >
      <div className="flex items-center gap-2 flex-shrink-0">
        <Filter className="w-4 h-4 text-[#00E5FF]" />
        <span className="text-xs text-[#A0AEC0] tracking-widest uppercase">{t("toolbar.filters")}</span>
      </div>

      {/* Text search */}
      <div className="flex-1 min-w-[200px]">
        <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">{t("toolbar.search_text")}</label>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A0AEC0] pointer-events-none" />
          <input
            data-testid="filter-text"
            type="text"
            value={filters.text}
            onChange={e => setFilters(p => ({ ...p, text: e.target.value }))}
            placeholder={t("toolbar.search_placeholder")}
            className="w-full bg-[#050505] border border-white/[0.04] text-white pl-7 pr-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]"
          />
        </div>
      </div>

      {/* Min Importance */}
      <div className="min-w-[130px]">
        <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">{t("toolbar.importance_ge")}</label>
        <select
          data-testid="filter-importance"
          value={filters.minImportance}
          onChange={e => setFilters(p => ({ ...p, minImportance: Number(e.target.value) }))}
          className="w-full bg-[#050505] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]"
        >
          <option value={0}>{t("common.all")}</option>
          <option value={5}>5 – {t("importance.5")}</option>
          <option value={4}>4 – {t("importance.4")}</option>
          <option value={3}>3 – {t("importance.3")}</option>
          <option value={2}>2 – {t("importance.2")}</option>
          <option value={1}>1 – {t("importance.1")}</option>
        </select>
      </div>

      {/* Sort */}
      <div className="min-w-[170px]">
        <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">{t("toolbar.sort_by")}</label>
        <select
          data-testid="filter-sort"
          value={filters.sort || "default"}
          onChange={e => setFilters(p => ({ ...p, sort: e.target.value }))}
          className="w-full bg-[#050505] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]"
        >
          <option value="default">{t("toolbar.sort_default")}</option>
          <option value="importance_desc">{t("toolbar.sort_imp_desc")}</option>
          <option value="importance_asc">{t("toolbar.sort_imp_asc")}</option>
          <option value="date_desc">{t("toolbar.sort_date_desc")}</option>
          <option value="date_asc">{t("toolbar.sort_date_asc")}</option>
        </select>
      </div>

      {/* Platform (SOCMINT) */}
      <div className="min-w-[140px]">
        <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">
          {t("toolbar.platform")} <span className="text-[#00FF66]">(SOCMINT)</span>
        </label>
        <select
          data-testid="filter-platform"
          value={filters.platform}
          onChange={e => setFilters(p => ({ ...p, platform: e.target.value }))}
          className="w-full bg-[#050505] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00FF66]"
        >
          <option value="all">{t("common.all")}</option>
          {PLATFORMS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </div>

      {/* Category (OSINT) */}
      <div className="min-w-[140px]">
        <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">
          {t("toolbar.category")} <span className="text-[#00E5FF]">(OSINT)</span>
        </label>
        <select
          data-testid="filter-category"
          value={filters.category}
          onChange={e => setFilters(p => ({ ...p, category: e.target.value }))}
          className="w-full bg-[#050505] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]"
        >
          <option value="all">{t("common.all")}</option>
          {OSINT_CATS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Channel (HUMINT) */}
      <div className="min-w-[140px]">
        <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">
          {t("toolbar.channel")} <span className="text-[#FF9F0A]">(HUMINT)</span>
        </label>
        <select
          data-testid="filter-channel"
          value={filters.channel}
          onChange={e => setFilters(p => ({ ...p, channel: e.target.value }))}
          className="w-full bg-[#050505] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#FF9F0A]"
        >
          <option value="all">{t("common.all_m")}</option>
          {HUMINT_CHANNELS_ALL.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {active && (
        <button
          data-testid="filter-reset"
          onClick={reset}
          className="flex items-center gap-1 text-[10px] text-[#FF3B30] hover:text-[#CC2F26] tracking-widest uppercase px-2 py-1.5 border border-[#FF3B30]/30 hover:border-[#FF3B30] transition-colors"
        >
          <X className="w-3 h-3" /> {t("common.reset")}
        </button>
      )}

      {counts && (
        <div className="w-full flex flex-wrap gap-4 text-[10px] text-[#A0AEC0] tracking-widest uppercase pt-2 border-t border-white/[0.04]">
          <span><span className="text-[#00E5FF]">OSINT</span>: {counts.osint.visible}/{counts.osint.total}</span>
          <span><span className="text-[#00FF66]">SOCMINT</span>: {counts.socmint.visible}/{counts.socmint.total}</span>
          <span><span className="text-[#FF9F0A]">HUMINT</span>: {counts.humint.visible}/{counts.humint.total}</span>
        </div>
      )}
    </div>
  );
}

// Predicate builders usable by parent pages
export function buildOsintFilter(filters) {
  const q = (filters.text || "").toLowerCase().trim();
  return (row) => {
    if (filters.minImportance && (Number(row.importance) || 0) < filters.minImportance) return false;
    if (filters.category !== "all" && row.category !== filters.category) return false;
    if (q) {
      const hay = `${row.title || ""} ${row.source || ""} ${row.excerpt || ""} ${row.url || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
}

export function buildSocmintFilter(filters) {
  const q = (filters.text || "").toLowerCase().trim();
  return (row) => {
    if (filters.minImportance && (Number(row.importance) || 0) < filters.minImportance) return false;
    if (filters.platform !== "all" && row.platform !== filters.platform) return false;
    if (q) {
      const hay = `${row.username || ""} ${row.public_info || ""} ${row.url || ""} ${row.platform || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
}

export function buildHumintFilter(filters) {
  const q = (filters.text || "").toLowerCase().trim();
  return (row) => {
    if (filters.minImportance && (Number(row.importance) || 0) < filters.minImportance) return false;
    if (filters.channel !== "all" && row.channel !== filters.channel) return false;
    if (q) {
      const hay = `${row.event_name || ""} ${row.contact_person || ""} ${row.notes || ""} ${row.outcome || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
}

// Sort comparator builder. Returns null for 'default' (panels keep natural order).
export function buildSort(filters) {
  switch (filters.sort) {
    case "importance_desc":
      return (a, b) => (Number(b.importance) || 0) - (Number(a.importance) || 0);
    case "importance_asc":
      return (a, b) => (Number(a.importance) || 0) - (Number(b.importance) || 0);
    case "date_desc":
      return (a, b) => (b.date || "").localeCompare(a.date || "");
    case "date_asc":
      return (a, b) => (a.date || "").localeCompare(b.date || "");
    default:
      return null;
  }
}
